import { injectable } from "inversify";
import {
  ApiException,
  CPLAuthTokenModel,
  ClientPortalSessionTokensModel,
  ExternalAccessTokenModel,
} from "./api/cplauthapi/CPLAuthAPI";
import { LoginType } from "global/enums";
import {
  clearActiveLoginType,
  getActiveLoginType,
  ISessionStore,
  sessionStoreFor,
  setActiveLoginType,
} from "./loginTypeStorage";
import { postAuthTokenToServiceWorker } from "./serviceWorkerAuthToken";

export interface ISessionService {
  getCPLSessionToken(): Promise<string>;
  setSessionTokens(tokens: ClientPortalSessionTokensModel, loginType: LoginType): Promise<void>;
  clearSessionTokens(): Promise<void>;
  refreshCPLToken(refreshClaims?: boolean): Promise<void>;
  setExternalAccessSessionTokens(externalAccessTokens: ExternalAccessTokenModel, singleUseToken: string): void;
}

type RefreshAPITokenProps =
  | {
      body: ExternalAccessTokenModel;
      singleUseToken: string;
      refreshClaims?: boolean;
    }
  | {
      body: CPLAuthTokenModel;
      singleUseToken?: never;
    };

// Keyed by token kind + LoginType so a Normal-login refresh in flight can never be mistaken for
// (or awaited in place of) an Impersonation refresh in the same tab, and vice versa.
const refreshTokenPromises: Partial<Record<string, Promise<void> | null>> = {};

@injectable()
export class SessionService implements ISessionService {
  readonly cplAuthTokenKey = "cplauthsessiontoken";
  readonly cplAuthRefreshTokenKey = "cplauthsessionrefreshtoken";
  readonly cplAuthTokenExpiryKey = "cplauthsessiontokenexpiry";
  readonly cplTokenKey = "cplsessiontoken";
  readonly cplRefreshTokenKey = "cplsessionrefreshtoken";
  readonly cplTokenExpiryKey = "cplsessiontokenexpiry";
  readonly cplImageTokenCookieName = "cplimagetoken";
  readonly cplImageRefreshTokenKey = "cplimagerefreshtoken";
  readonly cplImageTokenExpiryKey = "cplimagetokenexpiry";
  readonly cplExternalAccessTokenKey = "cplexternalaccesstoken";
  readonly cplSingleUseTokenKey = "cplsingleusetoken";
  readonly cplExternalAccessTokenExpiryKey = "cplexternalccesstokenexpiry";

  async getCPLSessionToken(): Promise<string> {
    const loginType = getActiveLoginType();

    switch (loginType) {
      case LoginType.Normal:
      case LoginType.Impersonation:
        return await this.getCPLOrImpersonationToken(loginType);
      case LoginType.ExternalAccess:
        return await this.getExternalAccessToken();
    }
  }

  private async getCPLOrImpersonationToken(loginType: LoginType.Normal | LoginType.Impersonation): Promise<string> {
    const store = sessionStoreFor(loginType);

    await this.refreshIfExpired(
      store,
      this.cplImageRefreshTokenKey,
      this.cplImageTokenExpiryKey,
      `image:${loginType}`,
      () => this.refreshCPLImageToken(loginType)
    );

    const refreshed = await this.refreshIfExpired(
      store,
      this.cplRefreshTokenKey,
      this.cplTokenExpiryKey,
      `cpl:${loginType}`,
      () => this.refreshCPLTokenForType(loginType)
    );

    const token = store.get(this.cplTokenKey) ?? "";
    if (refreshed) {
      postAuthTokenToServiceWorker(token);
    }
    return token;
  }

  private async getExternalAccessToken(): Promise<string> {
    const store = sessionStoreFor(LoginType.ExternalAccess);

    await this.refreshIfExpired(
      store,
      this.cplExternalAccessTokenKey,
      this.cplExternalAccessTokenExpiryKey,
      "external",
      () => this.refreshCPLExternalAccessToken()
    );

    return store.get(this.cplExternalAccessTokenKey) ?? "";
  }

  private async refreshIfExpired(
    store: ISessionStore,
    refreshTokenKey: string,
    expiryKey: string,
    cacheKey: string,
    refresh: () => Promise<void>
  ): Promise<boolean> {
    if (!store.get(refreshTokenKey) || !this.tokenExpired(store, expiryKey)) {
      return false;
    }

    if (!refreshTokenPromises[cacheKey]) {
      refreshTokenPromises[cacheKey] = refresh();
      await refreshTokenPromises[cacheKey];
      refreshTokenPromises[cacheKey] = null;
    } else {
      await refreshTokenPromises[cacheKey];
    }
    return true;
  }

  async setSessionTokens(tokens: ClientPortalSessionTokensModel, loginType: LoginType): Promise<void> {
    setActiveLoginType(loginType);
    this.setCPLToken(tokens.cplapiToken, loginType);
    await this.setCPLImageToken(tokens.imageToken, loginType);
  }

  async clearSessionTokens(): Promise<void> {
    const loginType = getActiveLoginType();
    const store = sessionStoreFor(loginType);

    store.remove(this.cplAuthTokenKey);
    store.remove(this.cplAuthRefreshTokenKey);
    store.remove(this.cplAuthTokenExpiryKey);
    store.remove(this.cplTokenKey);
    store.remove(this.cplRefreshTokenKey);
    store.remove(this.cplTokenExpiryKey);
    store.remove(this.cplExternalAccessTokenKey);
    store.remove(this.cplSingleUseTokenKey);
    store.remove(this.cplExternalAccessTokenExpiryKey);
    const currentImageToken = store.get(this.cplImageTokenCookieName);
    store.remove(this.cplImageRefreshTokenKey);
    store.remove(this.cplImageTokenExpiryKey);
    store.remove(this.cplImageTokenCookieName);
    clearActiveLoginType();
    if (currentImageToken !== null) {
      await this.deleteImageCookie(currentImageToken);
    }
  }

  setExternalAccessSessionTokens(externalAccessTokens: ExternalAccessTokenModel, singleUseToken: string): void {
    setActiveLoginType(LoginType.ExternalAccess);
    const store = sessionStoreFor(LoginType.ExternalAccess);
    store.set(this.cplExternalAccessTokenKey, externalAccessTokens.token ?? "");
    store.set(this.cplSingleUseTokenKey, singleUseToken);
    this.setTokenExpiry(store, this.cplExternalAccessTokenExpiryKey, externalAccessTokens.expiresIn);
  }

  async refreshCPLToken(refreshClaims?: boolean): Promise<void> {
    const loginType = getActiveLoginType();
    if (loginType === LoginType.ExternalAccess) {
      throw new Error("refreshCPLToken is not supported for an external access session.");
    }

    return this.refreshCPLTokenForType(loginType, refreshClaims);
  }

  private async refreshCPLTokenForType(
    loginType: LoginType.Normal | LoginType.Impersonation,
    refreshClaims?: boolean
  ): Promise<void> {
    const store = sessionStoreFor(loginType);
    const token = store.get(this.cplTokenKey) ?? "";
    const refreshToken = store.get(this.cplRefreshTokenKey) ?? "";
    const tokens = await this.refreshApiToken({ body: new CPLAuthTokenModel({ token, refreshToken, refreshClaims }) });
    return this.setCPLToken(tokens, loginType);
  }

  private async refreshCPLImageToken(loginType: LoginType.Normal | LoginType.Impersonation): Promise<void> {
    const store = sessionStoreFor(loginType);
    const token = store.get(this.cplImageTokenCookieName) ?? "";
    const refreshToken = store.get(this.cplImageRefreshTokenKey) ?? "";
    const tokens = await this.refreshApiToken({ body: new CPLAuthTokenModel({ token, refreshToken }) });
    return await this.setCPLImageToken(tokens, loginType);
  }

  private async refreshApiToken({ body, singleUseToken }: RefreshAPITokenProps): Promise<CPLAuthTokenModel> {
    const url = `${process.env.REACT_APP_CPL_AUTH_API_BASE_URI}/api/Authentication/${
      singleUseToken === undefined
        ? "RefreshTokenForClientPortalUser"
        : `RefreshTokenForCPLExternalAccessUser?singleUseToken=${encodeURIComponent(singleUseToken)}`
    }`.replace(/[?&]$/, "");

    const content = JSON.stringify(body);
    const options: RequestInit = {
      body: content,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/plain",
        Timeout: "300",
      },
    };

    const response = await fetch(url, options);
    const cplAuthTokenModel = await this.processTokenRefreshResponse(
      response,
      singleUseToken === undefined ? CPLAuthTokenModel.fromJS : ExternalAccessTokenModel.fromJS
    );
    return cplAuthTokenModel;
  }

  private async refreshCPLExternalAccessToken(): Promise<void> {
    const store = sessionStoreFor(LoginType.ExternalAccess);
    const token = store.get(this.cplExternalAccessTokenKey) ?? "";
    const singleUseToken = store.get(this.cplSingleUseTokenKey) ?? "";
    const tokens = await this.refreshApiToken({
      body: new ExternalAccessTokenModel({ token }),
      singleUseToken,
    });
    return this.setExternalAccessSessionTokens(tokens, singleUseToken);
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  private async processTokenRefreshResponse<T extends CPLAuthTokenModel | ExternalAccessTokenModel>(
    response: Response,
    fromJS: (data: any) => T
  ): Promise<CPLAuthTokenModel> {
    const status = response.status;
    const _headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => (_headers[k] = v));
    }
    if (status === 200) {
      const _responseText = await response.text();
      let result200: any = null;
      const resultData200 = _responseText === "" ? null : JSON.parse(_responseText);
      result200 = fromJS(resultData200);
      return result200;
    } else if (status !== 200 && status !== 204) {
      const _responseText_1 = await response.text();
      throw new ApiException("An unexpected server error occurred.", status, _responseText_1, _headers, null);
    }
    return Promise.resolve<T>(null as any);
  }
  /* eslint-enable @typescript-eslint/no-explicit-any */

  private setCPLToken(token: CPLAuthTokenModel | undefined, loginType: LoginType) {
    const store = sessionStoreFor(loginType);
    store.set(this.cplTokenKey, token?.token ?? "");
    store.set(this.cplRefreshTokenKey, token?.refreshToken ?? "");
    this.setTokenExpiry(store, this.cplTokenExpiryKey, token?.expiresIn);
  }

  private async setCPLImageToken(token: CPLAuthTokenModel | undefined, loginType: LoginType) {
    const store = sessionStoreFor(loginType);
    store.set(this.cplImageTokenCookieName, token?.token ?? "");
    store.set(this.cplImageRefreshTokenKey, token?.refreshToken ?? "");
    this.setTokenExpiry(store, this.cplImageTokenExpiryKey, token?.expiresIn);
    await this.setImageCookie(token?.token ?? "");
  }

  private setTokenExpiry(store: ISessionStore, storageKey: string, expiresIn: number | undefined) {
    const expiry = Date.now() + (expiresIn ?? 0) * 1000;
    store.set(storageKey, expiry.toString());
  }

  private tokenExpired(store: ISessionStore, expiryKey: string): boolean {
    const expiry = parseInt(store.get(expiryKey) ?? "0");
    return expiry < Date.now();
  }

  private async setImageCookie(value: string) {
    const endpoint = `ReflectImageCookie?imageToken=${encodeURIComponent(value)}`;
    await this.makeCookieRequest(endpoint, value);
  }

  private async deleteImageCookie(currentValue: string) {
    const endpoint = "DeleteImageCookie";
    await this.makeCookieRequest(endpoint, currentValue);
  }

  private async makeCookieRequest(endpoint: string, bearerToken: string) {
    const url = `${process.env.REACT_APP_CPL_API_BASE_URI}/api/CookieReflection/${endpoint}`;
    const options: RequestInit = {
      method: "POST",
      headers: {
        Authorization: `Bearer ${bearerToken}`,
        "Content-Type": "application/json",
        Accept: "text/plain",
        Timeout: "300",
      },
      credentials: "include",
    };
    await fetch(url, options);
  }
}
